/**
 * Certificate of Analysis Section - JavaScript
 *
 * Purpose: Handle PDF lazy loading, error states, and user interactions
 *
 * Features:
 * - Lazy load PDF only when section is near viewport (performance optimization)
 * - IntersectionObserver for efficient scroll detection
 * - Graceful error handling with user-friendly messages
 * - Loading states and visual feedback
 * - Cross-browser compatibility
 * - Memory-efficient cleanup
 * - No external dependencies (vanilla JavaScript)
 *
 * Browser Support: Modern browsers with IntersectionObserver
 * Fallback: Loads PDF immediately if IntersectionObserver not supported
 *
 * @author Certificate of Analysis Section
 * @version 1.0.0
 */

(function () {
  'use strict';

  /**
   * Configuration object for PDF viewer
   */
  const CONFIG = {
    // Root margin for IntersectionObserver - load PDF before section is fully visible
    rootMargin: '200px 0px',

    // Threshold for visibility (0.1 = 10% visible)
    threshold: 0.1,

    // Maximum time to wait for PDF load before showing error (30 seconds)
    loadTimeout: 30000,

    // Retry attempts if PDF fails to load
    maxRetries: 2,

    // Delay between retries (milliseconds)
    retryDelay: 2000,
  };

  /**
   * Main initialization function
   * Called when DOM is ready
   */
  function init() {
    // Find all COA sections on the page
    const coaSections = document.querySelectorAll('.coa-section');

    if (coaSections.length === 0) {
      // No COA sections on this page, exit early
      return;
    }

    // Process each section
    coaSections.forEach((section) => {
      setupPDFViewer(section);
    });
  }

  /**
   * Set up PDF viewer with lazy loading
   *
   * @param {HTMLElement} section - The COA section element
   */
  function setupPDFViewer(section) {
    const pdfViewer = section.querySelector('.coa-pdf-viewer');

    if (!pdfViewer) {
      console.error('COA Section: PDF viewer element not found');
      return;
    }

    const pdfUrl = pdfViewer.getAttribute('data-pdf-url');

    if (!pdfUrl || pdfUrl.trim() === '') {
      console.error('COA Section: PDF URL not provided');
      showError(section, 'PDF URL not configured');
      return;
    }

    // Check if IntersectionObserver is supported
    if ('IntersectionObserver' in window) {
      // Use lazy loading for performance
      setupLazyLoading(section, pdfViewer, pdfUrl);
    } else {
      // Fallback: Load PDF immediately for older browsers
      console.warn('COA Section: IntersectionObserver not supported, loading PDF immediately');
      loadPDF(section, pdfViewer, pdfUrl);
    }
  }

  /**
   * Set up lazy loading using IntersectionObserver
   *
   * @param {HTMLElement} section - The COA section element
   * @param {HTMLElement} pdfViewer - The PDF viewer container
   * @param {string} pdfUrl - URL of the PDF file
   */
  function setupLazyLoading(section, pdfViewer, pdfUrl) {
    // Create IntersectionObserver
    const observer = new IntersectionObserver(
      (entries, observerInstance) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Section is now visible (or near viewport), load the PDF
            loadPDF(section, pdfViewer, pdfUrl);

            // Stop observing this section (only load once)
            observerInstance.unobserve(entry.target);
          }
        });
      },
      {
        root: null, // Use viewport as root
        rootMargin: CONFIG.rootMargin,
        threshold: CONFIG.threshold,
      }
    );

    // Start observing the section
    observer.observe(section);
  }

  /**
   * Load PDF into viewer
   *
   * @param {HTMLElement} section - The COA section element
   * @param {HTMLElement} pdfViewer - The PDF viewer container
   * @param {string} pdfUrl - URL of the PDF file
   * @param {number} retryCount - Current retry attempt (default: 0)
   */
  function loadPDF(section, pdfViewer, pdfUrl, retryCount = 0) {
    // Show loading indicator
    const loadingIndicator = section.querySelector('.coa-pdf-loading');
    if (loadingIndicator) {
      loadingIndicator.classList.remove('hidden');
      loadingIndicator.setAttribute('aria-busy', 'true');
    }

    // Hide error message if it was shown previously
    const errorMessage = section.querySelector('.coa-pdf-error');
    if (errorMessage) {
      errorMessage.style.display = 'none';
    }

    try {
      // Create wrapper with iframe and embed elements
      const wrapper = createPDFIframe(pdfUrl);
      const iframe = wrapper.querySelector('iframe');
      const embed = wrapper.querySelector('embed');

      // Set up load handlers
      let loadTimeout;
      let loadingHideTimeout;
      let hasLoaded = false;

      /**
       * Handle successful PDF load
       */
      const handleLoad = () => {
        if (hasLoaded) return; // Prevent duplicate calls
        hasLoaded = true;

        // Clear timeouts
        if (loadTimeout) {
          clearTimeout(loadTimeout);
        }
        if (loadingHideTimeout) {
          clearTimeout(loadingHideTimeout);
        }

        // Hide loading indicator
        if (loadingIndicator) {
          loadingIndicator.classList.add('hidden');
          loadingIndicator.setAttribute('aria-busy', 'false');

          // Announce to screen readers
          announceToScreenReader('Certificate of Analysis loaded successfully');
        }

        // Mark viewer as loaded
        pdfViewer.classList.add('loaded');
      };

      /**
       * Handle PDF load error
       */
      const handleError = (error) => {
        if (hasLoaded) return; // Already loaded successfully
        hasLoaded = true;

        // Clear timeouts
        if (loadTimeout) {
          clearTimeout(loadTimeout);
        }
        if (loadingHideTimeout) {
          clearTimeout(loadingHideTimeout);
        }

        console.error('COA Section: PDF failed to load', error);

        // Attempt retry if we haven't exceeded max retries
        if (retryCount < CONFIG.maxRetries) {
          console.log(`COA Section: Retrying PDF load (attempt ${retryCount + 1}/${CONFIG.maxRetries})`);

          // Remove failed wrapper
          const existingWrapper = pdfViewer.querySelector('div');
          if (existingWrapper && existingWrapper.parentNode) {
            existingWrapper.parentNode.removeChild(existingWrapper);
          }

          // Retry after delay
          setTimeout(() => {
            loadPDF(section, pdfViewer, pdfUrl, retryCount + 1);
          }, CONFIG.retryDelay);

          return;
        }

        // All retries failed, show error message
        showError(section, 'Unable to load PDF. Please try downloading the document.');
      };

      // Attach event listeners to both iframe and embed
      if (iframe) {
        iframe.addEventListener('load', handleLoad);
        iframe.addEventListener('error', handleError);
      }

      if (embed) {
        embed.addEventListener('load', handleLoad);
        embed.addEventListener('error', handleError);
      }

      // Append wrapper to viewer first
      pdfViewer.appendChild(wrapper);

      // IMPORTANT: PDF load events are unreliable in iframes/embeds
      // Hide loading indicator after 4 seconds regardless of load event
      // This ensures users see the PDF even if the load event doesn't fire
      loadingHideTimeout = setTimeout(() => {
        if (!hasLoaded) {
          console.log('COA Section: Hiding loading indicator after timeout (PDF should be visible)');
          if (loadingIndicator) {
            loadingIndicator.classList.add('hidden');
            loadingIndicator.setAttribute('aria-busy', 'false');
          }
          pdfViewer.classList.add('loaded');
          hasLoaded = true;
        }
      }, 4000);

      // Set longer timeout for actual errors (PDF completely failed to load)
      loadTimeout = setTimeout(() => {
        if (!hasLoaded) {
          console.error('COA Section: PDF load timeout');
          handleError(new Error('PDF load timeout'));
        }
      }, CONFIG.loadTimeout);

    } catch (error) {
      console.error('COA Section: Error creating PDF viewer', error);
      showError(section, 'An error occurred while loading the PDF.');
    }
  }

  /**
   * Create iframe element for PDF display
   * Uses multiple fallback strategies for maximum browser compatibility
   *
   * @param {string} pdfUrl - URL of the PDF file
   * @returns {HTMLElement} - Configured iframe or embed element
   */
  function createPDFIframe(pdfUrl) {
    // Add view parameters for better browser compatibility
    const separator = pdfUrl.includes('?') ? '&' : '?';
    const pdfUrlWithParams = `${pdfUrl}${separator}#view=FitH&toolbar=0&navpanes=0&scrollbar=1`;

    // Try iframe first (works in most modern browsers)
    const iframe = document.createElement('iframe');

    // Set iframe attributes
    iframe.src = pdfUrlWithParams;
    iframe.title = 'Certificate of Analysis PDF Document';
    iframe.setAttribute('aria-label', 'Certificate of Analysis PDF Document Viewer');

    // Security attributes - allow-downloads for download functionality
    iframe.setAttribute('sandbox', 'allow-same-origin allow-scripts allow-popups allow-forms allow-downloads');

    // Loading attribute for additional lazy loading hint
    iframe.setAttribute('loading', 'lazy');

    // Allow fullscreen for better mobile experience
    iframe.setAttribute('allowfullscreen', 'true');

    // Style attributes (CSS will handle most styling)
    iframe.style.border = 'none';
    iframe.style.width = '100%';
    iframe.style.display = 'block';

    // Create wrapper with fallback embed
    const wrapper = document.createElement('div');
    wrapper.style.position = 'relative';
    wrapper.style.width = '100%';
    wrapper.style.height = '100%';

    // Add iframe
    wrapper.appendChild(iframe);

    // Add embed as fallback (for Safari and older browsers)
    const embed = document.createElement('embed');
    embed.src = pdfUrlWithParams;
    embed.type = 'application/pdf';
    embed.style.width = '100%';
    embed.style.height = '100%';
    embed.style.border = 'none';
    embed.style.display = 'none'; // Initially hidden
    embed.setAttribute('aria-label', 'Certificate of Analysis PDF Document');

    wrapper.appendChild(embed);

    // Detect if PDF failed to load in iframe and switch to embed
    setTimeout(() => {
      try {
        // If iframe is still empty after 3 seconds, try embed
        if (!iframe.contentWindow || iframe.contentWindow.length === 0) {
          iframe.style.display = 'none';
          embed.style.display = 'block';
        }
      } catch (e) {
        // If we can't access iframe content, use embed
        iframe.style.display = 'none';
        embed.style.display = 'block';
      }
    }, 3000);

    return wrapper;
  }

  /**
   * Show error message to user
   *
   * @param {HTMLElement} section - The COA section element
   * @param {string} message - Error message to display
   */
  function showError(section, message) {
    // Hide loading indicator
    const loadingIndicator = section.querySelector('.coa-pdf-loading');
    if (loadingIndicator) {
      loadingIndicator.classList.add('hidden');
      loadingIndicator.setAttribute('aria-busy', 'false');
    }

    // Show error message
    const errorElement = section.querySelector('.coa-pdf-error');
    if (errorElement) {
      errorElement.style.display = 'block';

      // Update error message if provided
      const errorText = errorElement.querySelector('.coa-pdf-error__text');
      if (errorText && message) {
        errorText.textContent = message;
      }

      // Announce error to screen readers
      announceToScreenReader(`Error: ${message}`);
    } else {
      // No error element found, log to console
      console.error('COA Section: Error element not found. Message:', message);
    }
  }

  /**
   * Announce message to screen readers using ARIA live region
   *
   * @param {string} message - Message to announce
   */
  function announceToScreenReader(message) {
    // Create or find announcement element
    let announcer = document.getElementById('coa-screen-reader-announcer');

    if (!announcer) {
      // Create announcer if it doesn't exist
      announcer = document.createElement('div');
      announcer.id = 'coa-screen-reader-announcer';
      announcer.setAttribute('role', 'status');
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');

      // Visually hidden but accessible to screen readers
      announcer.style.position = 'absolute';
      announcer.style.left = '-10000px';
      announcer.style.width = '1px';
      announcer.style.height = '1px';
      announcer.style.overflow = 'hidden';

      document.body.appendChild(announcer);
    }

    // Clear previous message
    announcer.textContent = '';

    // Set new message (after a brief delay for screen readers to detect change)
    setTimeout(() => {
      announcer.textContent = message;
    }, 100);
  }

  /**
   * Feature detection for PDF support in browser
   * Note: This is not 100% reliable across all browsers
   *
   * @returns {boolean} - Whether browser likely supports inline PDF viewing
   */
  function supportsPDFViewing() {
    // Check for common PDF MIME type support
    const mimeType = 'application/pdf';

    // Check if browser has PDF plugin/handler
    if (navigator.mimeTypes && navigator.mimeTypes[mimeType]) {
      return true;
    }

    // Additional check for modern browsers
    // Most modern browsers support PDF viewing natively
    const isModernBrowser = 'IntersectionObserver' in window && 'Promise' in window;

    return isModernBrowser;
  }

  /**
   * Clean up function (called when section is removed from DOM)
   * This is important for single-page applications or dynamic content
   *
   * @param {HTMLElement} section - The COA section element
   */
  function cleanup(section) {
    const pdfViewer = section.querySelector('.coa-pdf-viewer');

    if (pdfViewer) {
      // Remove wrapper and all children to free memory
      const wrapper = pdfViewer.querySelector('div');
      if (wrapper) {
        // Clear iframe and embed sources first
        const iframe = wrapper.querySelector('iframe');
        const embed = wrapper.querySelector('embed');

        if (iframe) {
          iframe.src = '';
        }
        if (embed) {
          embed.src = '';
        }

        // Remove wrapper
        if (wrapper.parentNode) {
          wrapper.parentNode.removeChild(wrapper);
        }
      }
    }
  }

  /**
   * Debug function - log section information
   * Only runs in development (when browser console is open)
   */
  function debugInfo() {
    if (typeof console !== 'undefined' && console.log) {
      console.log('COA Section initialized', {
        config: CONFIG,
        sections: document.querySelectorAll('.coa-section').length,
        supportsIntersectionObserver: 'IntersectionObserver' in window,
        supportsPDF: supportsPDFViewing(),
      });
    }
  }

  // ============================================================================
  // Initialization
  // ============================================================================

  /**
   * Initialize when DOM is ready
   */
  if (document.readyState === 'loading') {
    // DOM is still loading, wait for DOMContentLoaded
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM is already ready, initialize immediately
    init();
  }

  /**
   * Re-initialize on Shopify section load (for theme editor)
   * This ensures the section works when merchants preview changes
   */
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      // Only re-initialize if this is a COA section
      if (event.target && event.target.querySelector('.coa-section')) {
        init();
      }
    });

    document.addEventListener('shopify:section:unload', function (event) {
      // Clean up when section is removed in theme editor
      const section = event.target.querySelector('.coa-section');
      if (section) {
        cleanup(section);
      }
    });
  }

  // Log debug info in development
  debugInfo();

  // Export cleanup function for potential external use
  // (e.g., if this code is used in a single-page application)
  if (typeof window !== 'undefined') {
    window.COASection = {
      cleanup: cleanup,
      init: init,
      version: '1.0.0',
    };
  }

})();
